import React, { useState, useCallback, useEffect } from 'react'
import styled from 'styled-components'
import theme from '../styles/theme'
import UserSearch from './UserSearch'
import UserProfile from './UserProfile'
import AcademySection from './AcademySection'
import CustomAttributes from './CustomAttributes'
import OffersSection from './OffersSection'
import PurchaseHistory from './PurchaseHistory'
import Reservations from './Reservations'
import ChallengeData from './ChallengeData'
import TabContainer from './TabContainer'
import CreateProfileForm, { CreateProfileFormData } from './CreateProfileForm'
import UserMenu from './auth/UserMenu'
import { useAuth } from './auth/AuthProvider'
import { getUserBySessionMId, createUser } from '../services/api'

const AppContainer = styled.div`
  min-height: 100vh;
  background: #1a1a1a;
  padding: 2rem;
  overflow-x: visible;
`

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 3rem;
  max-width: 1000px;
  margin-left: auto;
  margin-right: auto;
`

const LogoContainer = styled.div`
  text-align: center;
  flex: 1;
  background: ${theme.color.background.light};
  border-radius: 8px;
  padding: 2rem;
  margin: 0 1rem;
`

const AppLogo = styled.img`
  max-height: 120px;
  max-width: 400px;
  height: auto;
  width: auto;
  object-fit: contain;
`

const UserMenuContainer = styled.div`
  min-width: 200px;
  display: flex;
  justify-content: flex-end;
`

const MainContent = styled.main`
  max-width: 1400px;
  margin-left: auto;
  margin-right: auto;
  background: rgba(255, 0, 0, 0.05);
`

const UserDataContainer = styled.div`
  margin-top: 2rem;
`

const CustomAttributesSubsection = styled.div`
  margin-top: 3rem;
  opacity: 0.8;
  transform: scale(0.95);

  h2 {
    color: ${theme.color.text.grey};
    font-size: 2rem;
  }
`

const LoadingOverlay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  background: ${theme.color.background.light};
  border: 1px solid ${theme.color.border.light};
  border-radius: 8px;
  box-shadow: inset 0 2px 4px ${theme.color.shadow.light};
  min-height: 400px;
`

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid ${theme.color.border.light};
  border-top: 4px solid ${theme.color.accent.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`

const LoadingText = styled.div`
  font-size: 1.4rem;
  color: ${theme.color.text.grey};
  text-align: center;
`

const AdminApp: React.FC = () => {
  const { isSuperAdmin, isAdmin } = useAuth()
  const [userData, setUserData] = useState<any>(null)
  const [refreshTimestamp, setRefreshTimestamp] = useState<number>(Date.now())
  const [showCreateProfile, setShowCreateProfile] = useState(false)
  const [createProfileEmail, setCreateProfileEmail] = useState('')
  const [createProfileLoading, setCreateProfileLoading] = useState(false)
  const [createProfileError, setCreateProfileError] = useState('')
  const [createProfileSuccess, setCreateProfileSuccess] = useState('')
  const [urlEmailParam, setUrlEmailParam] = useState<string | null>(null)
  const [isProfileDataLoading, setIsProfileDataLoading] = useState(false)
  const [marianaTekData, setMarianaTekData] = useState<any>(null)
  const [barrysPlatformData, setBarrysPlatformData] = useState<any>(null)
  const [reservationsData, setReservationsData] = useState<any>(null)
  const isRefreshingRef = React.useRef(false)
  const lastRefreshTimeRef = React.useRef<number>(0)

  // Check for email URL parameter on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const emailParam = urlParams.get('email')
    if (emailParam) {
      setUrlEmailParam(emailParam)
    }
  }, [])

  const handleUserFound = (data: any) => {
    setUserData(data)
    setShowCreateProfile(false)
    setCreateProfileEmail('')
    setCreateProfileError('')
    setCreateProfileSuccess('')
    setMarianaTekData(null)
    setBarrysPlatformData(null)
    setReservationsData(null)
  }

  const handleCreateProfile = (email: string) => {
    setCreateProfileEmail(email)
    setShowCreateProfile(true)
    setCreateProfileError('')
    setCreateProfileSuccess('')
  }

  const handleCreateProfileSubmit = async (formData: CreateProfileFormData) => {
    setCreateProfileLoading(true)
    setCreateProfileError('')
    setCreateProfileSuccess('')

    try {
      const newUser = await createUser(formData)
      setCreateProfileSuccess('User profile created successfully!')

      setTimeout(() => {
        handleUserFound(newUser)
      }, 2000)

    } catch (err) {
      setCreateProfileError(err instanceof Error ? err.message : 'Failed to create user profile')
    } finally {
      setCreateProfileLoading(false)
    }
  }

  const handleCreateProfileCancel = () => {
    setShowCreateProfile(false)
    setCreateProfileEmail('')
    setCreateProfileError('')
    setCreateProfileSuccess('')
  }

  const handleUpdate = useCallback(async () => {
    const clickTime = Date.now()
    console.log('handleUpdate called at:', clickTime)

    if (!userData?.user?.id) {
      console.log('No user data, skipping refresh')
      return
    }

    const now = Date.now()
    const timeSinceLastRefresh = now - lastRefreshTimeRef.current
    const MIN_REFRESH_INTERVAL = 2000

    if (timeSinceLastRefresh < MIN_REFRESH_INTERVAL) {
      return
    }

    if (isRefreshingRef.current) {
      return
    }

    isRefreshingRef.current = true
    lastRefreshTimeRef.current = now

    try {
      setRefreshTimestamp(Date.now())

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Refresh timeout after 30 seconds')), 30000)
      )

      const updatedData = await Promise.race([
        getUserBySessionMId(userData.user.id),
        timeoutPromise
      ])

      setUserData(updatedData)
    } catch (err) {
      console.error('Failed to refresh user data:', err)
    } finally {
      isRefreshingRef.current = false
    }
  }, [userData])

  return (
    <AppContainer>
      <Header>
        <div style={{ minWidth: '200px' }}></div>
        <LogoContainer>
          <AppLogo src="/portal-logo.png" alt="Barry's International Admin Portal" />
        </LogoContainer>
        <UserMenuContainer>
          <UserMenu />
        </UserMenuContainer>
      </Header>

      <MainContent>
        <UserSearch
          onUserFound={handleUserFound}
          onCreateProfile={handleCreateProfile}
          urlEmailParam={urlEmailParam}
          userData={userData}
          onRefresh={handleUpdate}
        />

        {showCreateProfile && (
          <CreateProfileForm
            initialEmail={createProfileEmail}
            onSubmit={handleCreateProfileSubmit}
            onCancel={handleCreateProfileCancel}
            isLoading={createProfileLoading}
            error={createProfileError}
            success={createProfileSuccess}
          />
        )}

        {userData && !showCreateProfile && (
          <UserDataContainer>
            <TabContainer
              tabs={[
                {
                  id: 'profile',
                  label: 'User Profile',
                  content: isProfileDataLoading ? (
                    <LoadingOverlay>
                      <LoadingSpinner />
                      <LoadingText>Loading user profile data...</LoadingText>
                    </LoadingOverlay>
                  ) : (
                    <div>
                      <UserProfile
                        userData={userData}
                        refreshTrigger={refreshTimestamp}
                        onLoadingStateChange={setIsProfileDataLoading}
                        onExternalDataLoaded={(mtData, bpData, reservData) => {
                          setMarianaTekData(mtData)
                          setBarrysPlatformData(bpData)
                          setReservationsData(reservData)
                        }}
                        onUpdate={handleUpdate}
                      />
                      <CustomAttributesSubsection>
                        <CustomAttributes userData={userData} />
                      </CustomAttributesSubsection>
                    </div>
                  )
                },
                {
                  id: 'academy',
                  label: 'The Academy',
                  content: (
                    <AcademySection userData={userData} onUpdate={handleUpdate} refreshTimestamp={refreshTimestamp} />
                  )
                },
                {
                  id: 'offers',
                  label: 'Current Offers',
                  content: isProfileDataLoading ? (
                    <LoadingOverlay>
                      <LoadingSpinner />
                      <LoadingText>Loading user profile data...</LoadingText>
                    </LoadingOverlay>
                  ) : (
                    <OffersSection userData={userData} onUpdate={handleUpdate} />
                  )
                },
                {
                  id: 'purchases',
                  label: 'Purchases',
                  content: isProfileDataLoading ? (
                    <LoadingOverlay>
                      <LoadingSpinner />
                      <LoadingText>Loading user profile data...</LoadingText>
                    </LoadingOverlay>
                  ) : (
                    <PurchaseHistory userData={userData} onUpdate={handleUpdate} refreshTimestamp={refreshTimestamp} />
                  )
                },
                {
                  id: 'reservations',
                  label: 'Reservations',
                  content: isProfileDataLoading ? (
                    <LoadingOverlay>
                      <LoadingSpinner />
                      <LoadingText>Loading user profile data...</LoadingText>
                    </LoadingOverlay>
                  ) : (
                    <Reservations userData={userData} reservationsData={reservationsData} />
                  )
                },
                {
                  id: 'challenge-data',
                  label: 'Challenge Data',
                  content: isProfileDataLoading ? (
                    <LoadingOverlay>
                      <LoadingSpinner />
                      <LoadingText>Loading user profile data...</LoadingText>
                    </LoadingOverlay>
                  ) : (
                    <ChallengeData userData={userData} reservationsData={reservationsData} />
                  )
                },
              ]}
              defaultTab="profile"
            />
          </UserDataContainer>
        )}
      </MainContent>
    </AppContainer>
  )
}

export default AdminApp